/**
 * Theme Context
 *
 * Provides theme context for Smoothie UI components using signals.
 * This enables reactive theme switching throughout the component tree.
 *
 * @module @smoothie-framework/ui/theme
 */
import { signal } from '../state';
import { defaultTheme } from '@smoothie-framework/ui-core';
export const DEFAULT_THEME_CSS_PREFIX = '--smth';
/**
 * Global theme signal
 * Components can subscribe to this for reactive theme updates
 */
const themeSignal = signal(defaultTheme);
const themeCssPrefixSignal = signal(DEFAULT_THEME_CSS_PREFIX);
/**
 * Get the current theme signal
 *
 * @returns Signal containing the current theme
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const theme = useTheme();
 *   const accentColor = theme().colors.blue[500];
 *
 *   return <Box background={accentColor}>...</Box>;
 * }
 * ```
 */
export function useTheme() {
    return themeSignal;
}
/**
 * Set the global theme
 *
 * @param theme - New theme to apply
 *
 * @example
 * ```typescript
 * import { setTheme } from '@smoothie-framework/ui';
 * import { extendTheme } from '@smoothie-framework/ui-core/theme';
 *
 * const darkTheme = extendTheme({
 *   colors: {
 *     blue: { 500: '#60a5fa' }
 *   }
 * }).theme;
 *
 * setTheme(darkTheme);
 * ```
 */
export function setTheme(theme) {
    themeSignal.set(theme);
}
export function setThemeCssPrefix(prefix) {
    const normalizedPrefix = prefix.trim();
    themeCssPrefixSignal.set(normalizedPrefix.length > 0 ? normalizedPrefix : DEFAULT_THEME_CSS_PREFIX);
}
/**
 * Update the current theme
 *
 * @param updater - Function that receives current theme and returns updated theme
 *
 * @example
 * ```typescript
 * import { updateTheme } from '@smoothie-framework/ui';
 *
 * updateTheme(current => ({
 *   ...current,
 *   colors: {
 *     ...current.colors,
 *     blue: { ...current.colors.blue, 500: '#custom' }
 *   }
 * }));
 * ```
 */
export function updateTheme(updater) {
    themeSignal.update(updater);
}
/**
 * Get the current theme value (non-reactive)
 *
 * @returns Current theme object
 *
 * @example
 * ```typescript
 * const theme = getTheme();
 * console.log(theme.colors.blue[500]);
 * ```
 */
export function getTheme() {
    return themeSignal();
}
export function getThemeCssPrefix() {
    return themeCssPrefixSignal();
}
//# sourceMappingURL=theme_context.js.map